// Data-table tools: P10 Customer Profitability, P11 Portfolio X-Ray, P14 Price Ladder.
import { useMemo, useState } from "react";
import { customerProfitability, portfolioXray, priceLadder, inr } from "./engines";
import { Note, ResultRow } from "./ui";

function EditableTable({ cols, rows, setRows, testid, addLabel = "+ Add row", defaults }) {
  const setC = (i, k) => (e) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } : r)));
  return (
    <div>
      <div className="overflow-x-auto border border-zinc-200 bg-white">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-black/10">
              {cols.map((c) => <th key={c.k} className="py-2 px-2 font-medium">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-black/5">
                {cols.map((c) => (
                  <td key={c.k} className="py-1 px-2">
                    {c.type === "checkbox" ? (
                      <input type="checkbox" checked={!!r[c.k]} onChange={setC(i, c.k)} data-testid={`${testid}-${i}-${c.k}`} />
                    ) : (
                      <input
                        type={c.type || "text"}
                        className="w-full min-w-[70px] px-1.5 py-1 text-sm border border-transparent hover:border-zinc-200 focus:border-black outline-none bg-transparent"
                        value={r[c.k]}
                        onChange={setC(i, c.k)}
                        data-testid={`${testid}-${i}-${c.k}`}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setRows((rs) => [...rs, { ...defaults }])} className="mt-3 text-sm font-medium underline underline-offset-4" data-testid={`${testid}-add`}>{addLabel}</button>
    </div>
  );
}

export function ToolP10() {
  const [rows, setRows] = useState([
    { id: "Customer A", revenue: 1000000, productCost: 600000, discounts: 100000, freight: 50000, service: 100000, credit: 20000 },
    { id: "Customer B", revenue: 500000, productCost: 250000, discounts: 20000, freight: 20000, service: 10000, credit: 5000 },
    { id: "Customer C", revenue: 800000, productCost: 560000, discounts: 120000, freight: 60000, service: 70000, credit: 30000 },
  ]);
  const r = useMemo(() => customerProfitability(rows.filter((x) => Number(x.revenue) > 0)), [rows]);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div>
        <p className="text-sm text-zinc-600 mb-3">One row per account. Use anonymised labels — this never leaves your browser.</p>
        <EditableTable
          testid="p10" rows={rows} setRows={setRows}
          defaults={{ id: `Customer ${String.fromCharCode(68 + rows.length)}`, revenue: 0, productCost: 0, discounts: 0, freight: 0, service: 0, credit: 0 }}
          cols={[
            { k: "id", label: "Account" }, { k: "revenue", label: "Revenue ₹", type: "number" },
            { k: "productCost", label: "Product cost", type: "number" }, { k: "discounts", label: "Discounts", type: "number" },
            { k: "freight", label: "Freight", type: "number" }, { k: "service", label: "Service cost", type: "number" }, { k: "credit", label: "Credit cost", type: "number" },
          ]}
        />
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Account ranking by contribution</div>
          <div className="mt-3 space-y-0">
            {r.rows.map((row, i) => (
              <ResultRow key={row.id} label={`${i + 1}. ${row.id} (${inr(row.revenue)})`} value={`${inr(row.contribution)} · ${row.marginPct}%`} tone={row.contribution < 0 ? "bad" : undefined} />
            ))}
            <ResultRow strong label="Portfolio average margin" value={`${r.avgMarginPct}%`} />
          </div>
        </div>
        <div className="mt-4 border border-zinc-200 bg-white p-5">
          <div className="overline mb-2">Action guide</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[["grow", "High revenue, healthy margin — protect and expand"], ["reprice", "High revenue, thin margin — reprice or renegotiate cost-to-serve"], ["service-efficiently", "Small but profitable — serve efficiently"], ["review", "Small and thin — review the service model"]].map(([k, d]) => (
              <div key={k} className="border border-black/10 p-3"><span className="font-semibold uppercase text-[10px] tracking-wider">{k}</span><p className="mt-1 text-zinc-600">{d}</p></div>
            ))}
          </div>
          <div className="mt-3"><Note>Never auto-terminate an account from allocation alone — cost allocations are estimates. A "review" flag means investigate, not exit.</Note></div>
        </div>
      </div>
    </div>
  );
}

const P11_GROUP_LABELS = { hero: "Heroes", maintain: "Maintain", fix: "Fix the economics", investigate: "Investigate (strategic role)", "retire-review": "Retire-review" };

export function ToolP11() {
  const [rows, setRows] = useState([
    { sku: "SKU-A100", revenue: 5000000, contributionPct: 35, growthPct: 12, turns: 8, strategic: false },
    { sku: "SKU-B200", revenue: 2000000, contributionPct: 22, growthPct: 5, turns: 5, strategic: false },
    { sku: "SKU-C300", revenue: 150000, contributionPct: 8, growthPct: -15, turns: 1, strategic: false },
    { sku: "SKU-D400", revenue: 90000, contributionPct: 4, growthPct: 0, turns: 1, strategic: true },
  ]);
  const r = useMemo(() => portfolioXray(rows.filter((x) => x.sku)), [rows]);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div>
        <EditableTable
          testid="p11" rows={rows} setRows={setRows}
          defaults={{ sku: `SKU-${rows.length + 1}`, revenue: 0, contributionPct: 0, growthPct: 0, turns: 0, strategic: false }}
          cols={[
            { k: "sku", label: "SKU" }, { k: "revenue", label: "Net sales ₹", type: "number" },
            { k: "contributionPct", label: "Contrib. %", type: "number" }, { k: "growthPct", label: "Growth %", type: "number" },
            { k: "turns", label: "Stock turns", type: "number" }, { k: "strategic", label: "Strategic?", type: "checkbox" },
          ]}
        />
        <div className="mt-3"><Note>Tick "Strategic?" for channel-entry or bundle SKUs — they get investigated, never auto-killed. Low-selling is not automatically low-value.</Note></div>
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Portfolio read ({r.skuCount} SKUs)</div>
          <div className="mt-2 text-sm text-zinc-600">Portfolio average contribution: <b>{r.avgContributionPct}%</b> · Total net sales: <b>{inr(r.totalRevenue)}</b></div>
          <div className="mt-4 space-y-3">
            {Object.entries(P11_GROUP_LABELS).map(([g, label]) => (
              r.groups[g].length > 0 && (
                <div key={g} className="border border-black/10 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider">{label} ({r.groups[g].length})</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.groups[g].map((x) => (
                      <span key={x.sku} className="text-xs bg-zinc-100 px-2 py-1">{x.sku} · {x.sharePct}%</span>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ToolP14() {
  const [rows, setRows] = useState([
    { competitor: "Our brand", price: 549, packSize: 500, date: "2026-10", note: "MRP" },
    { competitor: "Competitor A", price: 299, packSize: 400, date: "2026-10", note: "Observed in GT" },
    { competitor: "Competitor B", price: 420, packSize: 500, date: "2026-09", note: "Marketplace" },
  ]);
  const r = useMemo(() => priceLadder(rows), [rows]);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div>
        <p className="text-sm text-zinc-600 mb-3">Enter prices you have actually observed, with the date and where. Unverified numbers get flagged, not believed.</p>
        <EditableTable
          testid="p14" rows={rows} setRows={setRows}
          defaults={{ competitor: "", price: 0, packSize: 1, date: "", note: "" }}
          cols={[
            { k: "competitor", label: "Brand" }, { k: "price", label: "Price ₹", type: "number" },
            { k: "packSize", label: "Pack (g/ml/units)", type: "number" }, { k: "date", label: "Observed" }, { k: "note", label: "Source / note" },
          ]}
        />
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Price ladder (per equivalent unit)</div>
          <div className="mt-3 space-y-2">
            {r.ladder.map((x, i) => (
              <div key={i} className="flex items-baseline justify-between border-b border-black/5 pb-2">
                <span className="text-sm">{x.competitor} <span className="text-xs text-zinc-400">({x.date || "undated"})</span></span>
                <span className="text-sm tabular-nums font-medium">{inr(x.price)} / ₹{x.perUnit.toFixed(2)} per unit</span>
              </div>
            ))}
          </div>
          {r.gaps.length > 0 && (
            <div className="mt-4">
              <div className="overline mb-2">Price-pack gaps (opportunities)</div>
              {r.gaps.map((g, i) => (
                <div key={i} className="text-sm text-zinc-700 border border-black/10 px-3 py-2 mb-2">
                  {g.between}: <b>₹{g.gapPerUnit.toFixed(2)}/unit ({g.gapPct}%)</b> unoccupied space
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3"><Note>A gap in the ladder is a hypothesis, not a verdict — check whether the unoccupied point fails on margin, pack economics or channel fit before launching into it.</Note></div>
      </div>
    </div>
  );
}
